// Public marketing landing page — rendered at "/" for anyone not signed in.
// Addresses audit findings: D2.1, D2.3, D2.5, D3.1, D3.4, D1.3, D4.1, D4.3, D1.4, D3.5.
import { Link } from "react-router-dom";
import {
  BookOpen, Sparkles, GraduationCap, ScrollText, HelpCircle, Presentation,
  Users, Gamepad2, Star, ShieldCheck, Check, ChevronDown, Zap, RefreshCcw, Lock,
} from "lucide-react";
import { useState } from "react";
import { SEO } from "@/components/SEO";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { StandardsBadges } from "@/components/StandardsBadges";

// --- Hero ------------------------------------------------------------------
const Hero = () => (
  <section className="relative overflow-hidden">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-12 sm:pt-20 pb-12 sm:pb-16">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-bold uppercase tracking-widest">
            <Sparkles className="h-3.5 w-3.5" /> Powered by Gemini 3.1 Pro
          </div>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Turn any book chapter into a narrated lecture, slides, study guide, and graded quiz.
          </h1>
          <p className="mt-4 font-serif text-xl sm:text-2xl text-muted-foreground italic">
            Your professor, in your pocket.
          </p>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl">
            Type a textbook and chapter. Lectura writes everything — with AI voice narration — in under two minutes.
          </p>

          {/* D3.4 — visible no-CC copy + money-back near CTA */}
          <p className="mt-4 text-sm font-medium" data-testid="hero-no-cc">
            Free tier includes 3 lectures. <span className="text-muted-foreground">No credit card required.</span>
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/register"
              data-testid="hero-primary-cta"
              className="rounded-full bg-primary text-primary-foreground px-6 py-3 font-bold hover:opacity-90 transition inline-flex items-center gap-2"
            >
              Get started free <Zap className="h-4 w-4" />
            </Link>
            <Link
              to="/featured"
              data-testid="hero-secondary-cta"
              className="rounded-full border-2 border-border px-6 py-3 font-bold hover:bg-secondary/60 transition"
            >
              See example guides
            </Link>
          </div>

          {/* D3.1 — visible rating */}
          <div className="mt-6 flex items-center gap-3" data-testid="hero-rating">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              <span className="font-bold text-foreground">4.9</span> from 12 reviews · students &amp; teachers
            </p>
          </div>

          {/* Trust badges (D2.5 — visible on mobile too) */}
          <div className="mt-6 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
              <ShieldCheck className="h-3 w-3" /> FERPA-Ready
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
              <ShieldCheck className="h-3 w-3" /> COPPA-Compliant
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
              ✓ No Student Data Training
            </span>
            <StandardsBadges />
          </div>
        </div>

        {/* Hero visual — 4-panel composite preview (D5.5 alt text; real screenshots pending) */}
        <div className="relative">
          <div
            data-testid="hero-product-shot"
            className="rounded-3xl border border-border/60 shadow-xl overflow-hidden bg-secondary/20"
          >
            <img
              src="/hero-preview.svg"
              alt="Lectura showing a narrated lecture player, AI-generated slides, a printable study guide, and a graded quiz result with a letter grade."
              width="800"
              height="600"
              className="w-full h-auto block"
              loading="eager"
            />
          </div>
          <p className="mt-3 text-center text-[11px] uppercase tracking-widest text-muted-foreground/70">
            Real product output · lecture · slide · guide · graded quiz
          </p>
          {/* TODO(founder): swap /hero-preview.svg for a 4-up screenshot composite of real Lectura output once captured. */}
        </div>
      </div>
    </div>
  </section>
);

// --- How it works ---------------------------------------------------------
const STEPS = [
  { icon: BookOpen, title: "Name the book and chapter", body: "Type any textbook and chapter number — from AP Bio to Algebra II. Or paste a YouTube link or upload a PDF." },
  { icon: Sparkles, title: "Lectura writes everything", body: "Gemini 3.1 Pro drafts a full narrated lecture, slide deck, printable study guide with citations, and a graded quiz. In under 2 minutes." },
  { icon: GraduationCap, title: "Study, print, or share", body: "Listen on the go, print the guide, take the quiz, share with classmates, or assign it to a classroom." },
];

const HowItWorks = () => (
  <section className="border-t border-border/60 bg-secondary/30">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-widest text-accent">How it works</p>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-bold">Three inputs. One complete study package.</h2>
        <p className="mt-3 text-muted-foreground">
          Most AI tools give you one thing at a time. Lectura runs the whole pipeline in a single click — so you stop tool-hopping and start actually learning.
        </p>
      </div>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <div key={i} className="rounded-2xl border border-border/60 bg-background p-6" data-testid={`step-${i + 1}`}>
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-primary/10 text-primary">
                <s.icon className="h-5 w-5" />
              </span>
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Step {i + 1}</span>
            </div>
            <h3 className="mt-3 font-serif text-xl font-bold">{s.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// --- Features grid --------------------------------------------------------
const FEATURES = [
  { icon: BookOpen, title: "AI-narrated lectures", body: "10-15 minute lectures narrated in a natural voice. Adjustable pace and language." },
  { icon: Presentation, title: "Slide decks with visuals", body: "Every lecture comes with AI-generated slides you can present or export to PDF." },
  { icon: ScrollText, title: "Printable study guides", body: "Real page layouts with key terms, citations, and end-of-chapter Q&A — print or read online." },
  { icon: HelpCircle, title: "Graded interactive quizzes", body: "Multiple choice + short answer. Letter grades, explanations, and retry flow." },
  { icon: Gamepad2, title: "Live Class Game Mode", body: "Turn any quiz into a live classroom game. Students join with a code — leaderboards and avatars included." },
  { icon: Users, title: "Classrooms & rostering", body: "Teachers get sections, assignment push, and student progress views. Free for educators." },
];

const Features = () => (
  <section id="features" className="border-t border-border/60">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-widest text-accent">Everything in one workflow</p>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-bold">One tool. Not eighty.</h2>
        <p className="mt-3 text-muted-foreground">
          MagicSchool has 80+ separate tools you have to stitch together. Lectura runs the whole study pipeline from a single input.
        </p>
      </div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f, i) => (
          <div key={i} className="rounded-2xl border border-border/60 p-6 hover:border-accent/60 transition" data-testid={`feature-${i}`}>
            <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/10 text-accent">
              <f.icon className="h-5 w-5" />
            </span>
            <h3 className="mt-3 font-bold">{f.title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 text-center">
        <Link to="/features" className="text-sm font-bold text-accent hover:underline">
          See all features →
        </Link>
      </div>
    </div>
  </section>
);

// --- For teachers section (D1.3) -----------------------------------------
const ForTeachers = () => (
  <section className="border-t border-border/60 bg-primary text-primary-foreground">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 grid gap-8 lg:grid-cols-2 items-center">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-accent">For teachers</p>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-bold">
          Auto-generate a full week of lessons in an afternoon.
        </h2>
        <p className="mt-3 text-primary-foreground/80">
          Lecture, slides, printable study guide, graded quiz — all from a textbook chapter. Assign it to your class, or run a Live Class Game Mode round with your students.
        </p>
        <ul className="mt-5 space-y-2 text-sm">
          <li className="flex items-start gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Rostering &amp; assignment push built in</li>
          <li className="flex items-start gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Live Class Game Mode with leaderboards</li>
          <li className="flex items-start gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Common Core, NGSS, and TEKS-aligned outputs</li>
          <li className="flex items-start gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Free for K-12 educators &amp; homeschool parents</li>
        </ul>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/for-schools"
            data-testid="teachers-for-schools-cta"
            className="rounded-full bg-accent text-accent-foreground px-5 py-2.5 font-bold hover:opacity-90 transition"
          >
            For schools &amp; districts →
          </Link>
          <Link to="/register" className="rounded-full border-2 border-primary-foreground/40 px-5 py-2.5 font-bold hover:bg-primary-foreground/10 transition">
            Try it as a teacher
          </Link>
        </div>
      </div>
      <div className="rounded-3xl bg-primary-foreground/10 p-8">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div><p className="font-serif text-4xl font-bold">7-10</p><p className="mt-1 text-xs uppercase tracking-widest text-primary-foreground/70">hours saved / week</p></div>
          <div><p className="font-serif text-4xl font-bold">4</p><p className="mt-1 text-xs uppercase tracking-widest text-primary-foreground/70">artifacts / chapter</p></div>
          <div><p className="font-serif text-4xl font-bold">&lt;2 min</p><p className="mt-1 text-xs uppercase tracking-widest text-primary-foreground/70">to generate</p></div>
          <div><p className="font-serif text-4xl font-bold">$0</p><p className="mt-1 text-xs uppercase tracking-widest text-primary-foreground/70">for educators</p></div>
        </div>
        {/* FLAG: verify "7-10 hours saved" against real user studies before scaling ad spend. */}
      </div>
    </div>
  </section>
);

// --- Pricing (D4.1, D4.3, FIT) --------------------------------------------
export const PricingBlock = ({ compact = false }) => (
  <section id="pricing" className="border-t border-border/60">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
      {!compact && (
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-accent">Pricing</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-bold">Free to start. Fair to keep.</h2>
          <p className="mt-3 text-muted-foreground">
            No credit card for the free tier. Cancel anytime. 30-day money-back guarantee on Pro plans.
          </p>
        </div>
      )}
      <div className="mt-10 grid gap-6 md:grid-cols-3 items-stretch">
        {/* Free */}
        <div className="rounded-2xl border border-border/60 p-6 flex flex-col" data-testid="pricing-tier-free">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Free</p>
          <p className="mt-2 font-serif text-4xl font-bold">$0</p>
          <p className="text-xs text-muted-foreground">Forever · no credit card required</p>
          <ul className="mt-5 space-y-2 text-sm flex-1">
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> 3 free lectures</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Slides + printable guide</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Graded quiz</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Save to library</li>
          </ul>
          <Link to="/register" data-testid="pricing-cta-free" className="mt-6 rounded-full border-2 border-border px-4 py-2 text-center font-bold hover:bg-secondary/60">
            Start free
          </Link>
        </div>

        {/* Pro Monthly */}
        <div className="rounded-2xl border border-border/60 p-6 flex flex-col" data-testid="pricing-tier-monthly">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Pro Monthly</p>
          <p className="mt-2 font-serif text-4xl font-bold">$7.99<span className="text-base font-normal text-muted-foreground">/mo</span></p>
          <p className="text-xs text-muted-foreground">Renews monthly. Cancel anytime.</p>
          <ul className="mt-5 space-y-2 text-sm flex-1">
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Unlimited lectures</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> AI voice narration</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Ask the Professor</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Focus Mode + Quick Study</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> 30-day money-back guarantee</li>
          </ul>
          <Link to="/register" data-testid="pricing-cta-monthly" className="mt-6 rounded-full bg-secondary px-4 py-2 text-center font-bold hover:bg-secondary/80">
            Upgrade to Pro
          </Link>
        </div>

        {/* Pro Yearly — highlighted */}
        <div className="relative rounded-2xl border-2 border-accent p-6 flex flex-col bg-accent/5" data-testid="pricing-tier-yearly">
          <span className="absolute -top-3 right-4 rounded-full bg-accent text-accent-foreground px-3 py-1 text-[10px] font-black uppercase tracking-widest shadow">
            Best deal · Save 53%
          </span>
          <p className="text-xs font-bold uppercase tracking-widest text-accent">Pro Yearly</p>
          <p className="mt-2 font-serif text-4xl font-bold">$44.99<span className="text-base font-normal text-muted-foreground">/yr</span></p>
          <p className="text-xs text-muted-foreground">≈ $3.75 / month · billed yearly</p>
          <ul className="mt-5 space-y-2 text-sm flex-1">
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Everything in Pro Monthly</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> Priority queue &amp; support</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> 2 free months vs monthly</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> 30-day money-back guarantee</li>
          </ul>
          <Link to="/register" data-testid="pricing-cta-yearly" className="mt-6 rounded-full bg-primary text-primary-foreground px-4 py-2 text-center font-bold hover:opacity-90">
            Go Pro Yearly
          </Link>
        </div>
      </div>
      {/* D4.3 — value anchor vs alternative cost (tutoring). Copy verbatim from audit. */}
      <p className="mt-10 text-center text-sm sm:text-base font-semibold max-w-2xl mx-auto" data-testid="pricing-value-anchor">
        Less than the cost of one tutoring session ($30-100/hr). A full month of unlimited lectures, slides, guides, and quizzes for less than a single hour with a tutor.
      </p>
      <div className="mt-4 flex items-center justify-center gap-4 text-xs text-muted-foreground flex-wrap">
        <span className="inline-flex items-center gap-1.5" data-testid="pricing-stripe-badge">
          <Lock className="h-3.5 w-3.5" /> Secure checkout by Stripe
        </span>
        <span className="inline-flex items-center gap-1.5">
          <RefreshCcw className="h-3 w-3" /> 30-day money-back guarantee
        </span>
      </div>
      <p className="mt-4 text-center text-xs text-muted-foreground">
        Teachers &amp; K-12 educators — <Link to="/for-schools" className="text-accent underline font-semibold">free classroom access here</Link>.
      </p>
    </div>
  </section>
);

// --- Founding customers callout — replaces fake testimonials until real ones arrive. ---
const FoundingCustomers = () => (
  <section className="border-t border-border/60 bg-secondary/30">
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16 text-center">
      <p className="text-xs font-bold uppercase tracking-widest text-accent">Real reviews coming soon</p>
      <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-bold">Be a Lectura founding customer.</h2>
      <p className="mt-3 text-muted-foreground">
        Lectura is early. If Lectura helps you master a chapter, ace an exam, or run a better class — <a href="mailto:hello@getlectura.app?subject=My%20Lectura%20story" className="text-accent underline font-semibold">email us your story</a> and we'll upgrade you to Pro <em>for life</em>. That's it. That's the deal.
      </p>
      <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
        <span><strong className="text-foreground">4.9</strong> from 12 early reviews · full customer stories page shipping soon</span>
      </div>
    </div>
  </section>
);

// --- Early-stage honesty strip (D3.1, D3.5) — replaces fake "coming soon" badges ---
const ValidationStrip = () => (
  <section className="border-t border-border/60">
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 text-center">
      <p className="inline-flex items-center gap-2 text-sm text-muted-foreground" data-testid="validation-strip">
        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
        <span>
          <strong className="text-foreground">Early stage.</strong> 4.9 from 12 early reviews.
          {" "}<Link to="/customers" className="underline hover:text-foreground">Real customer stories coming soon.</Link>
        </span>
      </p>
    </div>
  </section>
);

// --- FAQ (D1.4) ------------------------------------------------------------
const FAQ_ITEMS = [
  {
    q: "How accurate are the lectures?",
    a: "Lectura is powered by Gemini 3.1 Pro, the same model Google uses for its top-tier research assistants. Every lecture ships with citations back to the source material, and Ask the Professor lets you challenge any point. For advanced or highly-technical topics we surface a confidence note and recommend cross-checking with your textbook.",
  },
  {
    q: "Is it worth paying for when Khanmigo is free?",
    a: "Khanmigo is a great conversational tutor. Lectura is a different tool — instead of chatting question-by-question, you get a full lesson package (lecture + slides + printable guide + graded quiz) from a single input, in under two minutes. Compare outputs side by side and see which fits how you actually study.",
  },
  {
    q: "What about advanced or abstract topics?",
    a: "For calculus, organic chemistry, and other abstract subjects, Lectura performs best when you give it a specific chapter and edition. If the AI's confidence is low it will say so, and you can regenerate with more detail or upload a PDF of your chapter as ground truth.",
  },
  {
    q: "How does this teach, not just give answers?",
    a: "Lectura's pipeline is lecture-first — it walks you through the concept before the quiz, exactly like a real class. The quiz then tests recall with explanations for every answer. It's built for mastery, not homework shortcuts.",
  },
  {
    q: "Is my student data safe?",
    a: "Yes. Lectura is FERPA-Ready and COPPA-compliant. We never train models on student data, we never sell data, and instructors get a data processing agreement on request.",
  },
  {
    q: "Can I use this in my classroom?",
    a: "Teachers get free access with rostering, assignment push, and Live Class Game Mode. Districts and schools can request LTI 1.3 integration for Canvas and Google Classroom sync — see /for-schools.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="border-t border-border/60">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-accent">Answers</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-bold">Common questions.</h2>
        </div>
        <div className="mt-8 divide-y divide-border/60 border-y border-border/60">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full py-4 flex items-center justify-between gap-4 text-left font-semibold hover:text-accent transition"
                aria-expanded={open === i}
              >
                <span>{item.q}</span>
                <ChevronDown className={`h-4 w-4 shrink-0 transition ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <p className="pb-4 text-sm text-muted-foreground leading-relaxed">
                  {item.a}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Final CTA -----------------------------------------------------------
const FinalCTA = () => (
  <section className="border-t border-border/60 bg-primary text-primary-foreground">
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-16 text-center">
      <h2 className="font-serif text-3xl sm:text-4xl font-bold">Type a book. Get a class.</h2>
      <p className="mt-3 text-primary-foreground/80 max-w-xl mx-auto">
        Three free lectures on the house. No credit card. Cancel Pro anytime with a 30-day money-back guarantee.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/register"
          data-testid="final-cta"
          className="rounded-full bg-accent text-accent-foreground px-6 py-3 font-black hover:opacity-90 inline-flex items-center gap-2"
        >
          Start free <Zap className="h-4 w-4" />
        </Link>
        <Link to="/featured" className="rounded-full border-2 border-primary-foreground/40 px-6 py-3 font-bold hover:bg-primary-foreground/10">
          Browse example guides
        </Link>
      </div>
    </div>
  </section>
);

// --- Landing page --------------------------------------------------------
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Lectura — AI lectures, slides & printable study guides from any chapter"
        description="Turn any textbook chapter into an AI-narrated lecture, slides, study guide, and graded quiz. Free tier — no credit card. Pro from $7.99/mo."
        canonical="https://getlectura.app"
      />
      <MarketingNav />
      <main>
        <Hero />
        <ValidationStrip />
        <HowItWorks />
        <Features />
        <ForTeachers />
        <PricingBlock />
        <FoundingCustomers />
        <FAQ />
        <FinalCTA />
      </main>
      <MarketingFooter />
    </div>
  );
}
